import {
  Box,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Theme,
  Tooltip,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridLoadingOverlay,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import makeStyles from "@mui/styles/makeStyles";
import moment from "moment";
import { FC, useState, useEffect, useCallback } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  downloadVccIncidentReport,
  getClosedAndDeletedIncidentsRequest,
} from "../../../actions/vcc-incident";
import { ShortIdUtil } from "../../../utils/id-utils";
import { IRootReducer } from "../../../reducers";
import { JogNames } from "../../common/associated-jogs";

const useStyles = makeStyles((theme: Theme) => ({
  finalizeButton: {
    color: theme.palette.secondary.main,
  },
}));

export interface ReportGridRow {
  id: string;
  closedDate: string | Date;
  location: string;
  eventType: string;
  RecordsManagementStatus?: string;
  token?: string;
  link: string;
  finalizedBy?: string;
  finalizedDate?: string | Date;
  purgeDate?: string | Date;
  jogIds?: string;
}

interface ReportGridProps {
  props: {
    handleReportFinalize: (
      incidentId: string,
      recordRetWfToken: string
    ) => void;
  };
}

interface IncidentToFinalizeState {
  id: string;
  token: string;
}

const ReportGrid: FC<ReportGridProps> = ({ props }) => {
  const [gridData, setGridData] = useState([] as ReportGridRow[]);
  const [isGridLoading, setIsGridLoading] = useState(true);
  const [incidentToFinalized, setIncidentToFinalize] =
    useState<IncidentToFinalizeState>();
  const [displayConfirmation, setDisplayConfirmation] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0
  })
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const jogFilters = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences.jogFilters);
  const closedIncidentStore = useSelector((state: IRootReducer) => state.vccIncidentReducer.closedIncidents);
  const deletedIncidentStore = useSelector((state: IRootReducer) => state.vccIncidentReducer.deletedIncidents);

  const showLoadingAnimation = useCallback(() => {
    const timeoutId = setTimeout(() => {
      setIsGridLoading(false);
    }, 1250);

    return function cleanup() {
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    setIsGridLoading(true);
    let apiRows: ReportGridRow[] = [...closedIncidentStore.data, ...deletedIncidentStore.data]
      .filter((i) => i.hasReport === true)
      .sort(
        (d1, d2) =>
          new Date(d2.updatedDate).getTime() -
          new Date(d1.updatedDate).getTime()
      )
      .reduce(function (results: ReportGridRow[], r, index) {
        results.push({
          id: r.id,
          closedDate: r.updatedDate,
          location: r.location,
          eventType: r.type,
          RecordsManagementStatus: "",
          link: "link",
          token: r.recordRetWfToken,
          finalizedBy: r.finalizedBy,
          finalizedDate: r.finalizedDate,
          purgeDate: r.purgeDate,
          jogIds: r.jogIds
        });

        return results;
      }, []);
    setGridData([...apiRows]);
    showLoadingAnimation();
  }, [closedIncidentStore.data, deletedIncidentStore.data, showLoadingAnimation]);

  const handleFinalizeClick = (id: string, token: string) => {
    setDisplayConfirmation(true);
    setIncidentToFinalize({ id, token });
  };

  const handleFinalizeConfirm = () => {
    if (incidentToFinalized) {
      props.handleReportFinalize(
        incidentToFinalized.id,
        incidentToFinalized.token
      );
    }
    setIncidentToFinalize(undefined);
    setDisplayConfirmation(false);
  };

  const handleFinalizeCancel = () => {
    setIncidentToFinalize(undefined);
    setDisplayConfirmation(false);
  };

  const handleReportDownload = (
    id?: string,
    closedDate?: string,
    finalizedBy?: string
  ): void => {
    if (id) {
      if (closedDate) {
        const dateString = moment(closedDate).format("YYYY-MM-DD");
        let fileDisplayName = `${id.substring(0, 3)}-${id.substring(
          3,
          5
        )} Incident Report ${dateString}`;
        fileDisplayName += finalizedBy ? "FINAL.xlsx" : "DRAFT.xlsx";
        dispatch(downloadVccIncidentReport(id, fileDisplayName));
      } else dispatch(downloadVccIncidentReport(id));
    }
  };

  const handleIncidentPaging = () => {
    dispatch(getClosedAndDeletedIncidentsRequest(jogFilters.join(","), true));
  }

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Id",
      flex: 0.25,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => {
        const id = params.id;
        return (
          <Button
            type="button"
            color="primary"
            onClick={() => navigate(`/incident-model/${id}`)}
          >
            {ShortIdUtil(id as string)}
          </Button>
        );
      },
    },
    {
      field: "closedDate",
      headerName: "Closed Date",
      flex: 0.5,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const updatedDate = params.row.closedDate; //JCK Review: We should probably add an offical closed data, instead of trying to guess at it this way. Add a Closed Date to the VCC Incidents that is set in the API.
        return (
          <>{moment(updatedDate as string).format("YYYY-MM-DD HH:mm")}</>
        );
      },
    },
    {
      field: "location",
      headerName: "Location",
      flex: 0.5,
      filterable: false,
    },
    {
      field: "eventType",
      headerName: "Type",
      flex: 0.5,
      filterable: false,
    },
    {
      field: "finalizedBy",
      headerName: "Finalized By",
      flex: 0.5,
      filterable: false,
    },
    {
      field: "finalizedDate",
      headerName: "Finalized Date",
      flex: 0.5,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const finalizedDate = params.row.finalizedDate;
        return (
          <>{finalizedDate && moment(finalizedDate).format("YYYY-MM-DD HH:mm")}</>
        );
      },
    },
    {
      field: "purgeDate",
      headerName: "Deletion Date",
      flex: 0.4,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const purgeDate = params.row.purgeDate;
        if (purgeDate) {
          return (
            <Tooltip title={"Deletion Date: " + moment(purgeDate).format("YYYY-MM-DD HH:mm")}>
              <>{moment(purgeDate).fromNow()}</>
            </Tooltip>
          );
        }
      },
    },
    {
      field: "reports",
      headerName: "Reports",
      flex: 1,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => {
        const id = params.row.id;
        const closedDate = params.row.closedDate;
        const finalizedBy = params.row.finalizedBy;
        const token = params.row.token;
        return (
          <Box>
            <Button
              type="button"
              color="primary"
              onClick={() =>
                handleReportDownload(`${id}`, `${closedDate}`, `${finalizedBy}`)
              }
            >
              Download {finalizedBy !== "" ? "Finalized" : "Draft"} Report
            </Button>
          </Box>
        );
      },
    },
    {
      field: "jogIds",
      headerName: "Associated JOG(s)",
      flex: 1,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => {
        return <JogNames jogIds={ params.row.jogIds } maxJogDisplayCount={3} />        
      },
    },
  ];

  const isLoadMoreDisplayed: boolean = !!(closedIncidentStore.pagingKeys || deletedIncidentStore.pagingKeys);

  const getGridHeight = () => {
    const headerHeight = 64;
    const appMainBoxMargins = 16;
    const titleAndSubtitle = 86;
    const loadMoreButton = isLoadMoreDisplayed ? 48 : 0;
    const total = headerHeight + appMainBoxMargins + titleAndSubtitle + loadMoreButton;
    return `calc(100vh - ${total}px`;
  }

  return (
    <>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography variant="h6" component="h1">
            Closed VCC Incidents
          </Typography>
        </Grid>
        <Grid item m={1}>
          <Button
            aria-label="Reload Records"
            color="inherit"
            endIcon={<RefreshIcon />}
            size="medium"
            onClick={() => dispatch(getClosedAndDeletedIncidentsRequest(jogFilters.join(',')))}
            variant="contained"
            style={{ minWidth: "150px" }}
          >
            Refresh
          </Button>
        </Grid>
      </Grid>
      <Typography variant="body1" mb={1}>
        To request corrections to a draft report, please contact
        vccs@wsdot.wa.gov and include the incident ID in the subject
        line.
      </Typography>
      <Box style={{ height: getGridHeight() }}>
        <DataGrid
          columns={columns}
          density={"standard"}
          disableColumnSelector
          disableRowSelectionOnClick
          loading={isGridLoading}
          onPaginationModelChange={setPaginationModel}
          paginationModel={paginationModel}
          rows={gridData}
          slots={{
            loadingOverlay: GridLoadingOverlay,
          }}
        />
      </Box>
      {isLoadMoreDisplayed && (
        <Box mt={1} mb={1} style={{ float: "right" }}>
          <Button variant="contained" color="inherit" size="small" onClick={handleIncidentPaging}>
            Load More Events
          </Button>
        </Box>
      )}
      <Dialog
        open={displayConfirmation}
        onClose={() => setDisplayConfirmation(false)}
      >
        <DialogTitle id="form-dialog-title">
          Finalize VCC Incident:
          {ShortIdUtil(incidentToFinalized?.id as string)}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Clicking "Submit" below will mark VCC Incident
            {ShortIdUtil(incidentToFinalized?.id as string)} as finalized, email
            the report to a designated distribution list, and delete the
            incident report from the VCC system 30 days from now on
            {moment().add(1, "M").format("LL")}.
          </DialogContentText>
          <DialogActions>
            <Button onClick={() => handleFinalizeCancel()} color="primary">
              Cancel
            </Button>
            <Button onClick={() => handleFinalizeConfirm()} color="primary">
              Submit
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </>
  );
};
export default ReportGrid;
